import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CohortRow, CohortScope } from '../../types/dashboard';
import { PROGRAM_TYPES, SPORTS } from '../../data/dimensions';
import { aggregateRetention, retentionTotal, type RetentionPoint } from '../../utils/retention';
import { comboProgram, comboSport } from '../../utils/combos';
import { aopById, territoryById } from '../../utils/territory';
import { COHORT_SEASONS } from '../../utils/calendar';
import { fmtInt, fmtPct, fmtPP } from '../../utils/format';
import { downloadCsv } from '../../utils/csv';
import { useSort } from '../../hooks/useSort';
import { Panel } from '../ui/Panel';
import { SegmentedControl } from '../ui/SegmentedControl';
import { SortableTh } from '../ui/SortableTh';
import { ExportButton } from '../ui/TableControls';
import { EmptyState } from '../ui/EmptyState';
type Dimension = 'territory' | 'aop' | 'sport' | 'program' | 'season';
type Key = 'name' | 'base' | 'retained' | 'rate' | 'vsNetwork' | 'yoy';
interface BreakdownRow {
  key: string;
  name: string;
  base: number;
  retained: number;
  rate: number;
  vsNetwork: number | null;
  yoy: number | null;
  link?: string;
}
const KEY_FNS: Record<Exclude<Dimension, 'season'>, (r: CohortRow) => string> = {
  territory: r => r.t,
  aop: r => r.a,
  sport: r => String(comboSport(r.k)),
  program: r => String(comboProgram(r.k))
};
function labelFor(dim: Dimension, key: string): {
  name: string;
  link?: string;
} {
  if (dim === 'territory') return {
    name: territoryById.get(key)?.name ?? key,
    link: `/territory/${key}`
  };
  if (dim === 'aop') {
    const e = aopById.get(key);
    return {
      name: e ? `${e.aop.name} · ${e.territory.name}` : key,
      link: e ? `/territory/${e.territory.id}/aop/${key}` : undefined
    };
  }
  if (dim === 'sport') return {
    name: SPORTS[Number(key)]
  };
  return {
    name: PROGRAM_TYPES[Number(key)]
  };
}
interface Props {
  scope: CohortScope;
  network: CohortScope;
  base: number;
  comp: number;
  within12: boolean;
  trend: RetentionPoint[];
  pairLabel: string;
}
export function RetentionBreakdown({
  scope,
  network,
  base,
  comp,
  within12,
  trend,
  pairLabel
}: Props) {
  const navigate = useNavigate();
  const [dim, setDim] = useState<Dimension>('territory');
  const rows = useMemo<BreakdownRow[]>(() => {
    if (dim === 'season') {
      const gap = comp - base;
      return trend.map(p => {
        const s = retentionTotal(scope, p.base, p.comp, within12);
        const prior = p.base - 4 >= 0 ? retentionTotal(scope, p.base - 4, p.comp - 4, within12) : null;
        return {
          key: String(p.base),
          name: within12 ? `${COHORT_SEASONS[p.base].label} → +12 months` : `${COHORT_SEASONS[p.base].label} → ${COHORT_SEASONS[p.base + gap].label}`,
          base: s.base,
          retained: s.retained,
          rate: s.rate,
          vsNetwork: p.network !== null && s.base ? s.rate - p.network : null,
          yoy: prior && prior.base ? s.rate - prior.rate : null
        };
      });
    }
    const keyFn = KEY_FNS[dim];
    const cur = aggregateRetention(scope, base, comp, within12, keyFn);
    const netGrouped = aggregateRetention(network, base, comp, within12, keyFn);
    const netTotal = retentionTotal(network, base, comp, within12);
    const prior = base - 4 >= 0 ? aggregateRetention(scope, base - 4, comp - 4, within12, keyFn) : null;
    const compareGrouped = dim === 'sport' || dim === 'program';
    return Array.from(cur.entries()).map(([key, a]) => {
      const p = prior?.get(key);
      const netRate = compareGrouped ? netGrouped.get(key)?.rate ?? null : netTotal.rate;
      return {
        key,
        ...labelFor(dim, key),
        base: a.base,
        retained: a.retained,
        rate: a.rate,
        vsNetwork: netRate === null ? null : a.rate - netRate,
        yoy: p && p.base ? a.rate - p.rate : null
      };
    });
  }, [dim, scope, network, base, comp, within12, trend]);
  const {
    sorted,
    sort,
    onSort
  } = useSort<BreakdownRow, Key>(rows, {
    key: dim === 'season' ? 'name' : 'rate',
    dir: dim === 'season' ? 'asc' : 'asc'
  }, (r, k) => r[k]);
  const exportCsv = () => downloadCsv(`i9-retention-by-${dim}.csv`, ['Group', 'Base cohort participants', 'Retained participants', 'Retention %', 'vs Network (pp)', 'YoY change (pp)'], sorted.map(r => [r.name, r.base, r.retained, (r.rate * 100).toFixed(2), r.vsNetwork === null ? '' : (r.vsNetwork * 100).toFixed(2), r.yoy === null ? '' : (r.yoy * 100).toFixed(2)]));
  const pp = (v: number | null) => v === null ? <span className="text-ink-subtle" data-id="element-423" data-mp="az9cf7erfyey">—</span> : <span className={v < 0 ? 'text-negative' : 'text-positive'} data-id="element-424" data-mp="b027vc89r1hs">{fmtPP(v)}</span>;
  return <Panel title="Retention breakdown" subtitle={dim === 'season' ? 'Each base season with the same comparison rule' : `${pairLabel} · ${dim === 'sport' || dim === 'program' ? 'vs network for the same group' : 'vs network total'}`} actions={<>
          <SegmentedControl ariaLabel="Breakdown dimension" value={dim} onChange={setDim} options={[{
      value: 'territory',
      label: 'Territory'
    }, {
      value: 'aop',
      label: 'AOP'
    }, {
      value: 'sport',
      label: 'Sport'
    }, {
      value: 'program',
      label: 'Program type'
    }, {
      value: 'season',
      label: 'Season'
    }]} data-id="element-426" data-mp="27y8a18kp65kt" />
          <ExportButton onClick={exportCsv} data-id="element-427" data-mp="1p0f5k1ufdnlb" />
        </>} bodyClassName="p-0" data-id="element-425" data-mp="617p2aaaxcxo">
      {sorted.length === 0 ? <EmptyState title="No participants in this cohort" description="The current scope has no participants in the selected base season." data-id="element-428" data-mp="1tzs1dc39y501" /> : <div className="max-h-[460px] overflow-auto" data-id="element-429" data-mp="22zet1667bwfw">
          <table className="w-full min-w-[760px] border-collapse text-[12.5px]" data-id="element-430" data-mp="3ac740pof7xlq">
            <thead className="sticky top-0 z-10 bg-navy-50 text-navy" data-id="element-431" data-mp="3ae9vzf94w0t3">
              <tr data-id="element-432" data-mp="3ec7ha6zsa2r7">
                <SortableTh label={dim === 'season' ? 'Cohort' : 'Group'} sortKey="name" sort={sort} onSort={onSort} align="left" className="pl-5" data-id="element-433" data-mp="3efvdjdwm539m" />
                <SortableTh label="Base participants" sortKey="base" sort={sort} onSort={onSort} data-id="element-434" data-mp="1aaqslfqaje9u" />
                <SortableTh label="Retained" sortKey="retained" sort={sort} onSort={onSort} data-id="element-435" data-mp="1o9yu4f9xeyh7" />
                <SortableTh label="Retention %" sortKey="rate" sort={sort} onSort={onSort} data-id="element-436" data-mp="1ja40i3yrnusg" />
                <SortableTh label="vs network" sortKey="vsNetwork" sort={sort} onSort={onSort} data-id="element-437" data-mp="vkjipoecn3kr" />
                <SortableTh label="YoY change" sortKey="yoy" sort={sort} onSort={onSort} className="pr-5" data-id="element-438" data-mp="rjzn4p9fc80q" />
              </tr>
            </thead>
            <tbody className="tabular-nums" data-id="element-439" data-mp="15cfhl1o9exq1">
              {sorted.map((r, i) => {
            const go = r.link ? () => navigate(`${r.link}?metric=retention`) : undefined;
            return <tr key={r.key} tabIndex={go ? 0 : undefined} onClick={go} onKeyDown={e => go && e.key === 'Enter' && go()} className={`border-b border-line ${go ? 'cursor-pointer hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline-none' : ''} ${i % 2 ? 'bg-canvas/50' : ''}`} data-id="element-440" data-mp="csqrnziubsfo" data-mpi={i}>
                    <td className="whitespace-nowrap py-2.5 pl-5 pr-3 font-medium text-navy" data-id="element-441" data-mp="2ag1cyx59t8qj">{r.name}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-442" data-mp="2feum6ddiydkw">{fmtInt(r.base)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-443" data-mp="337kvsfxygks0">{fmtInt(r.retained)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-ink" data-id="element-444" data-mp="377v43z97zna1">{fmtPct(r.rate)}</td>
                    <td className="px-3 py-2.5 text-right" data-id="element-445" data-mp="2t83s2429kgyo">{pp(r.vsNetwork)}</td>
                    <td className="py-2.5 pl-3 pr-5 text-right" data-id="element-446" data-mp="2y6nes6oolws3">{pp(r.yoy)}</td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
    </Panel>;
}